import type { Sessao } from './auth';
import { CABECALHO_CSRF } from './auth';
import type { Resultado } from './acesso';
import { buscar } from './buscar';

/**
 * BLOCO 8 — o front de /api/premiacao (telas 09, 10 e 15).
 *
 * 🚨 Nenhum valor de premiação é calculado aqui: o cheque vem pronto do servidor (motor com
 * paridade ao 1.0) e a tela só formata e soma o que já veio arredondado — e o PAGAMENTO é sempre
 * recalculado no servidor, nunca enviado.
 */

export type Programa = 'vitamina' | 'pre_vencido' | 'paradao';

export type Produto = {
  readonly id: string | null;
  readonly nome: string;
  readonly codigo: string | null;
};
export type Fatia = {
  readonly preco: number;
  readonly qtd: number;
  readonly comUn: number;
  readonly faixa: 'a' | 'b' | null;
  readonly devido: number;
  readonly motivo: string | null;
  readonly card: number | null;
};
type Base = {
  readonly pessoaId: string;
  readonly programa: Programa;
  readonly produto: Produto;
  readonly dia: string;
  readonly qtd: number;
  readonly precoUnit: number;
  readonly fatias: readonly Fatia[];
  readonly cupons: readonly string[];
  /** As vendas do HOS que casaram: cupom, hora, quantidade e preço. */
  readonly vendas?: readonly {
    readonly cupom: string;
    readonly hora: string | null;
    readonly qtd: number;
    readonly precoUnit: number;
  }[];
};
type DoLancamento = {
  readonly vendaIds: readonly string[];
  /** Foto do lançamento (id da evidência; abre em /api/evidencias/:id). */
  readonly foto: string | null;
  /** Lote informado no lançamento (pré-vencido). */
  readonly lote: string | null;
  readonly liberado: boolean;
  readonly jaPaga: boolean;
};
export type LinhaBate = Base &
  DoLancamento & {
    devido: number;
    /** Vitamina do mês cortada: o que esta linha pagaria (o `devido` vem 0). */
    cortadaPeloMes?: { readonly corteId: string; readonly valor: number };
    comUn: number | null;
    faixa: 'a' | 'b' | null;
    pago: number;
    motivo?: string | null;
  };
export type LinhaAbaixo = Base &
  DoLancamento & { pago: number; motivo: string | null; card: number | null };
export type LinhaDiaErrado = Base &
  DoLancamento & { lancouEm: string; pago: number; perdeu: number; dias: number };
export type LinhaNaoLancou = Base & { perdeu: number };
/** Pré-vencido vendido no mesmo dia em que o lote entrou no sistema (regra de 24/09/2026): perde. */
export type LinhaLoteDoMesmoDia = Base &
  DoLancamento & { loteCriadoEm: string; pago: number; perdeu: number };
export type LinhaNaoVendeu = DoLancamento & {
  pessoaId: string;
  programa: Programa;
  produto: Produto;
  dia: string;
  qtd: number;
  pago: number;
};
export type LinhaCancelada = {
  readonly programa: Programa;
  readonly vendaIds: readonly string[];
  readonly produto: Produto;
  readonly dia: string;
  readonly qtd: number;
  readonly pagoOrig: number;
  readonly motivo: string | null;
  readonly unEmBate: number;
  readonly desconto: number;
  readonly semPremio: boolean;
};
export type LinhaDevolvida = {
  readonly programa: Programa;
  readonly produto: Produto;
  readonly dia: string;
  readonly cupom: string;
  readonly qtd: number;
  readonly valor: number;
  readonly precoUnit: number;
  readonly desconto: number;
  /** Ajuste: o dia estava numa semana já paga sem ela (entra no fechamento seguinte, §5.3). */
  readonly deSemanaPaga?: true;
};
export type TotaisDoTipo = { devido: number; pago: number; cancelado: number; bruto: number };

/** Vitamina do mês cortada (código 30 / lote de outro produto — dono, 27/09/2026). */
export type CorteDoMes = {
  readonly id: string;
  readonly pessoaId: string;
  /** 'AAAA-MM' */
  readonly mes: string;
  readonly tipo: 'codigo_30' | 'lote_de_outro_produto';
  readonly detalhe: string;
  /** Vitamina do mês que já tinha sido paga: desconta neste cheque. */
  readonly descontoDeSemanaPaga: number;
};

export type ChequeDaPessoa = {
  readonly pessoaId: string;
  readonly nome: string;
  readonly codigo: string | null;
  readonly tipos: Readonly<Record<Programa, TotaisDoTipo>>;
  readonly devolucao: number;
  readonly cancelado: number;
  readonly perdeu: number;
  readonly cortado: number;
  readonly indevido: number;
  readonly devidoTotal: number;
  readonly pagoTotal: number;
  readonly rendeuBruto: number;
  readonly descontado: number;
  readonly aReceber: number;
  readonly itensLiquidos: number;
  readonly meta: number;
  readonly metaBatida: boolean;
  readonly faltouParaMeta: number;
  readonly zeradoPorMeta: boolean;
  /** Vitamina desta semana que o corte do mês zerou (já fora do a receber). */
  readonly cortadoNoMes?: number;
  /** Vitamina do mês cortado que já tinha sido paga: desconta aqui. */
  readonly descontoDeCorte?: number;
  readonly cortes?: readonly CorteDoMes[];
  readonly linhas: {
    readonly bate: readonly LinhaBate[];
    readonly naoLancou: readonly LinhaNaoLancou[];
    readonly naoVendeu: readonly LinhaNaoVendeu[];
    readonly diaErrado: readonly LinhaDiaErrado[];
    readonly loteDoMesmoDia: readonly LinhaLoteDoMesmoDia[];
    readonly abaixoDoCard: readonly LinhaAbaixo[];
    readonly devolvidos: readonly LinhaDevolvida[];
    readonly canceladas: readonly LinhaCancelada[];
  };
};

export type Pagamento = {
  readonly id: string;
  readonly accountId: string;
  readonly periodoDe: string;
  readonly periodoAte: string;
  readonly valor: string;
  readonly pagoEm: string;
  readonly pagoPor: string;
  readonly estornado: { readonly em: string; readonly por: string; readonly motivo: string } | null;
};

export type ChequeDaSemana = {
  readonly periodo: { readonly de: string; readonly ate: string };
  readonly pessoas: readonly ChequeDaPessoa[];
  readonly totais: {
    readonly porTipo: Readonly<
      Record<Programa, { devido: number; pago: number; cancelado: number }>
    >;
    readonly aReceber: number;
    readonly devolucao: number;
    readonly cancelado: number;
    readonly perdeu: number;
  };
  readonly avisos: {
    readonly diasSemRelatorio: readonly string[];
    readonly diasSemOperador: readonly string[];
    readonly confirmarQuemEQuem: readonly {
      nomeHos: string;
      vendas: number;
      palpiteId: string;
      palpiteNome: string;
    }[];
    readonly ponteContradita: readonly {
      nomeHos: string;
      ponteNome: string;
      devolucaoNome: string;
      devolucaoCodigo: string;
    }[];
    readonly operadoresPendentes: readonly {
      nomeHos: string;
      palpite: string | null;
      palpiteId: string | null;
      confianca: string;
    }[];
    readonly operadoresFora: readonly string[];
    readonly vendasSemPessoa: readonly { nome: string; vendas: number }[];
    readonly devolucaoSemPessoa: readonly { codigo: string; itens: number }[];
  };
  readonly foraDaConta: {
    readonly brindes: number;
    /** null para quem não é dono: é número da LOJA (vendas fora dos programas). */
    readonly semPrograma: number | null;
    readonly saldoZerado: number;
    /** `valor` null para quem não é dono (R$ de devolução da loja). */
    readonly devolucaoForaPrograma: { readonly itens: number; readonly valor: number | null };
  };
  readonly pagamentos: Readonly<Record<string, Pagamento | null>>;
  /** Bloco 11: premiação retida por regra de qualidade do Giro. */
  readonly retidas?: Readonly<Record<string, { readonly desde: string; readonly porque: string }>>;
  readonly emenda: {
    readonly anterior: { de: string; ate: string } | null;
    readonly buraco: readonly string[];
    readonly sobreposicao: readonly { de: string; ate: string }[];
  };
  readonly relatorioDoHos: { readonly dias: number; readonly ultimoDia: string | null };
  /** Nomes do HOS ligados sozinhos pelo código de quem vendeu (vendas da API). */
  readonly ponteAutomatica?: readonly {
    readonly nomeHos: string;
    readonly codigo: string;
    readonly pessoaId: string | null;
    readonly pessoaNome: string | null;
    readonly sistema: boolean;
  }[];
  /** Cada dia da semana: de onde veio a venda do HOS e as devoluções. */
  readonly fonteDoHos?: {
    readonly ultimaLeituraDaApi: string | null;
    /** Por que a leitura do HOS ainda não deixa pagar a semana (null = pode). */
    readonly leituraPendente?: string | null;
    readonly dias: readonly {
      readonly dia: string;
      /** null para quem não é dono: faturamento e cupons da loja são só dos donos. */
      readonly cupons: number | null;
      readonly valor: string | null;
      readonly fonte: 'api' | 'relatorio' | null;
      readonly devolucoes: number;
      readonly situacao: 'fechado' | 'em_andamento' | 'futuro' | 'sem_venda';
    }[];
  };
  readonly pagoNoMes: { readonly valor: string; readonly semanas: number };
};

export type MinhaPremiacao = {
  readonly semana: { readonly de: string; readonly ate: string };
  readonly cheque: ChequeDaPessoa | null;
  readonly pagamento: Pagamento | null;
  readonly jaRecebido: string;
  readonly pagamentos: readonly Pagamento[];
};

export type Escrita =
  | { readonly ok: true }
  | { readonly ok: false; readonly incerto: boolean; readonly mensagem: string };

async function ler<T>(caminho: string): Promise<Resultado<T>> {
  let r: Response;
  try {
    r = await buscar(caminho, {
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
    });
  } catch {
    return { ok: false, falha: { tipo: 'offline' } };
  }
  if (r.ok) return { ok: true, dados: (await r.json()) as T };
  if (r.status === 401) return { ok: false, falha: { tipo: 'sem-sessao' } };
  if (r.status === 403) return { ok: false, falha: { tipo: 'sem-acesso' } };
  return { ok: false, falha: { tipo: 'inesperado', codigo: r.headers.get('x-request-id') } };
}

async function escrever(
  sessao: Sessao,
  caminho: string,
  corpo: unknown,
  tipo = 'application/json',
): Promise<Escrita & { dados?: unknown }> {
  let r: Response;
  try {
    r = await fetch(caminho, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': tipo, [CABECALHO_CSRF]: sessao.csrfToken },
      body: tipo === 'application/json' ? JSON.stringify(corpo) : (corpo as BodyInit),
    });
  } catch {
    return {
      ok: false,
      incerto: true,
      mensagem: 'A conexão caiu no meio. Confira antes de repetir.',
    };
  }
  if (r.ok)
    return { ok: true, dados: r.status === 204 ? undefined : ((await r.json()) as unknown) };
  if (r.status >= 500)
    return {
      ok: false,
      incerto: true,
      mensagem: 'O servidor não confirmou. Confira antes de repetir.',
    };
  let mensagem = '';
  try {
    mensagem = ((await r.json()) as { error?: { message?: string } }).error?.message ?? '';
  } catch {
    // sem corpo
  }
  if (r.status === 403) mensagem = 'Sem permissão para isso.';
  return { ok: false, incerto: false, mensagem: mensagem || 'Não foi aceito.' };
}

export const lerSemana = (de: string, ate: string) =>
  ler<ChequeDaSemana>(`/api/premiacao/semana?de=${de}&ate=${ate}`);
export const lerSemanaSugerida = () =>
  ler<{ de: string; ate: string }>('/api/premiacao/semana-sugerida');
export const lerMinhaPremiacao = () => ler<MinhaPremiacao>('/api/premiacao/minha');

export const pagar = (sessao: Sessao, accountId: string, de: string, ate: string) =>
  escrever(sessao, '/api/premiacao/pagamentos', { accountId, de, ate });
export const estornar = (sessao: Sessao, id: string, motivo: string) =>
  escrever(sessao, `/api/premiacao/pagamentos/${id}/estorno`, { motivo });
export const liberar = (sessao: Sessao, lancamentoId: string, motivo: string) =>
  escrever(sessao, '/api/premiacao/liberacoes', { lancamentoId, motivo });
export const revogarLiberacao = (sessao: Sessao, lancamentoId: string, motivo: string) =>
  escrever(sessao, '/api/premiacao/liberacoes/revogar', { lancamentoId, motivo });
export const marcarLoteDeOutroProduto = (sessao: Sessao, lancamentoId: string, detalhe: string) =>
  escrever(sessao, '/api/premiacao/cortes/lote-de-outro-produto', { lancamentoId, detalhe });
export const desfazerCorte = (sessao: Sessao, id: string, motivo: string) =>
  escrever(sessao, `/api/premiacao/cortes/${id}/desfazer`, { motivo });

/** "2026-09" → "setembro de 2026". */
export function mesPorExtenso(mes: string): string {
  const [a, m] = mes.split('-');
  const nomes = [
    'janeiro',
    'fevereiro',
    'março',
    'abril',
    'maio',
    'junho',
    'julho',
    'agosto',
    'setembro',
    'outubro',
    'novembro',
    'dezembro',
  ];
  return `${nomes[Number(m) - 1] ?? m ?? ''} de ${a ?? ''}`;
}
export const MOTIVO_DO_CORTE: Record<CorteDoMes['tipo'], string> = {
  codigo_30: 'venda do código 30 (Mais Brasil / Formulativa) lançada no InovaPay',
  lote_de_outro_produto: 'venda lançada com lote de outro produto',
};

export const ligarOperador = (
  sessao: Sessao,
  nomeHos: string,
  accountId: string | null,
  fora: boolean,
) => escrever(sessao, '/api/premiacao/operadores', { nomeHos, accountId, fora });

/* ------------------------------------------------------------------ *
 * Formatos
 * ------------------------------------------------------------------ */

/** 1234.5 → 'R$ 1.234,50'. O número já vem arredondado ao centavo do servidor. */
export function reais(n: number): string {
  const neg = n < 0;
  const [i = '0', d = '00'] = Math.abs(n).toFixed(2).split('.');
  return `${neg ? '−' : ''}R$ ${i.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${d}`;
}
/** 3420 → 'R$ 3.420'; 164.5 → 'R$ 164,50' (cartões grandes). */
export function reaisCurto(n: number): string {
  const s = reais(n);
  return s.endsWith(',00') ? s.slice(0, -3) : s;
}
/** '2026-09-18','2026-09-24' → '18–24/09'. */
export function semanaCurta(de: string, ate: string): string {
  const [, m1, d1] = de.split('-');
  const [, m2, d2] = ate.split('-');
  return m1 === m2
    ? `${d1 ?? ''}–${d2 ?? ''}/${m2 ?? ''}`
    : `${d1 ?? ''}/${m1 ?? ''}–${d2 ?? ''}/${m2 ?? ''}`;
}
export const diaCurto = (dia: string) => dia.split('-').reverse().slice(0, 2).join('/');

/** A regra que gerou a linha, em português curto (coluna REGRA do design). */
export function regraDaLinha(l: LinhaBate): string {
  if (l.comUn === null) return 'liberado na mão';
  if (l.programa === 'paradao') return 'paradão';
  if (l.programa === 'pre_vencido')
    return l.fatias.every((f) => f.card !== null && f.preco <= f.card + 0.001)
      ? 'na base'
      : 'base + 50%';
  if (l.faixa === 'a') return `MÁX · ${reaisCurto(l.comUn)}/un`;
  if (l.faixa === 'b') return `MÍN · ${reaisCurto(l.comUn)}/un`;
  return 'entre os cards';
}

/** Composição da semana por produto: soma das linhas que pagam, e as que descontam. */
export function composicao(
  c: ChequeDaPessoa,
): { produto: string; qtd: number; regra: string; gerado: number; desconto: boolean }[] {
  const porProduto = new Map<
    string,
    { produto: string; qtd: number; regra: string; gerado: number; desconto: boolean }
  >();
  for (const l of c.linhas.bate) {
    const k = `${l.programa}|${l.produto.id ?? l.produto.nome}|${regraDaLinha(l)}`;
    const x = porProduto.get(k) ?? {
      produto: l.produto.nome,
      qtd: 0,
      regra: regraDaLinha(l),
      gerado: 0,
      desconto: false,
    };
    x.qtd += l.qtd;
    x.gerado = Math.round((x.gerado + l.devido + (l.cortadaPeloMes?.valor ?? 0)) * 100) / 100;
    porProduto.set(k, x);
  }
  // A linha que paga já sai do motor SEM a parte cancelada; o que foi cancelado volta para o
  // "gerado" do produto e sai de novo, visível, na linha "cancelada" — assim a soma da tabela
  // bate com o recibo (rendeu − canceladas − devoluções = total apurado) e nada sai duas vezes.
  for (const l of c.linhas.canceladas) {
    if (l.desconto <= 0) continue;
    const doProduto = [...porProduto.entries()].find(([k]) =>
      k.startsWith(`${l.programa}|${l.produto.id ?? l.produto.nome}|`),
    );
    if (doProduto) doProduto[1].gerado = Math.round((doProduto[1].gerado + l.desconto) * 100) / 100;
    else
      porProduto.set(`${l.programa}|${l.produto.id ?? l.produto.nome}|cancelada`, {
        produto: l.produto.nome,
        qtd: l.qtd,
        regra: 'antes de cancelar',
        gerado: l.desconto,
        desconto: false,
      });
  }
  const linhas = [...porProduto.values()].sort((a, b) => b.gerado - a.gerado);
  for (const l of c.linhas.canceladas)
    if (l.desconto > 0)
      linhas.push({
        produto: l.produto.nome,
        qtd: l.qtd,
        regra: 'cancelada',
        gerado: -l.desconto,
        desconto: true,
      });
  for (const l of c.linhas.devolvidos)
    if (l.desconto > 0)
      linhas.push({
        produto: l.produto.nome,
        qtd: l.qtd,
        regra: 'devolvida',
        gerado: -l.desconto,
        desconto: true,
      });
  // vitamina do mês cortada: a desta semana e a que já tinha sido paga naquele mês
  const cortadas = c.linhas.bate.filter((l) => l.cortadaPeloMes);
  if ((c.cortadoNoMes ?? 0) > 0)
    linhas.push({
      produto: 'Vitamina do mês cortada',
      qtd: cortadas.reduce((s, l) => s + l.qtd, 0),
      regra: 'corte do mês',
      gerado: -(c.cortadoNoMes ?? 0),
      desconto: true,
    });
  if ((c.descontoDeCorte ?? 0) > 0)
    linhas.push({
      produto: 'Vitamina do mês cortado já paga',
      qtd: 0,
      regra: 'corte do mês',
      gerado: -(c.descontoDeCorte ?? 0),
      desconto: true,
    });
  return linhas;
}

/* ------------------------------------------------------------------ *
 * As 12 regras da premiação (texto único: Conferência e Regras de premiação)
 * ------------------------------------------------------------------ */
export const REGRAS_DA_PREMIACAO: readonly { readonly titulo: string; readonly efeito: string }[] =
  [
    { titulo: 'Vendeu e não lançou', efeito: 'perde o prêmio da venda' },
    { titulo: 'Lançou e não vendeu', efeito: 'não paga (não há a venda no HOS)' },
    { titulo: 'Lançou em dia diferente da venda', efeito: 'perde, salvo liberado na Premiação' },
    {
      titulo: 'Pré-vencido vendido no dia em que o lote entrou no sistema',
      efeito: 'perde, salvo liberado (vale desde 25/09/2026)',
    },
    { titulo: 'Vendido abaixo do card', efeito: 'não premia' },
    { titulo: 'Cancelada pelo administrador', efeito: 'o prêmio vira desconto' },
    {
      titulo: 'Devolvida',
      efeito: 'desconta a comissão daquela venda, mesmo de outra semana',
    },
    { titulo: 'Meta semanal de itens não batida', efeito: 'zera a premiação da semana' },
    {
      titulo: 'Venda no HOS de quem não está ligado a uma pessoa do InovaPay',
      efeito: 'não paga ninguém até ligar',
    },
    { titulo: 'Venda além do saldo do lote do programa', efeito: 'é estoque normal: não premia' },
    { titulo: 'Retida pela qualidade do Giro', efeito: 'a premiação fica retida até regularizar' },
    {
      titulo: 'Ao lançar',
      efeito:
        'pré-vencido exige foto, lote existente com saldo e validade na janela; paradão exige foto ou observação',
    },
  ];

/**
 * Vendas de programa de nomes do HOS SEM pessoa — tirando quem já foi marcado "não é balconista"
 * (caixa, integrador, usuário de sistema): esses ficam fora da conta de propósito, não é pendência.
 */
export function vendasSemPessoaPendentes(
  s: ChequeDaSemana,
): readonly { readonly nome: string; readonly vendas: number }[] {
  const fora = new Set(s.avisos.operadoresFora);
  return s.avisos.vendasSemPessoa.filter((v) => !fora.has(v.nome) && v.vendas > 0);
}

/** Um pagamento do histórico (entre semanas), com o estorno quando houve. */
export interface PagamentoDoHistorico {
  readonly id: string;
  readonly accountId: string;
  readonly pessoa: string;
  readonly periodo: { readonly de: string; readonly ate: string };
  readonly valor: string;
  readonly pagoEm: string;
  readonly pagoPor: string | null;
  readonly estorno: {
    readonly em: string;
    readonly por: string | null;
    readonly motivo: string;
  } | null;
}
export const lerHistoricoDePagamentos = () =>
  ler<{ pagamentos: readonly PagamentoDoHistorico[] }>('/api/premiacao/pagamentos');
